import { http } from "@shared/utils/request";
import type { BatchResultResponse } from "@shared/utils/batchResult";
import { resolveCombinedSearchForRequest, resolveMultiSelectForRequest } from "@shared/utils/filterQuery";
import { toAmazonVcWarehouseExportRequestParams, toAmazonVcWarehouseListRequestParams, type AmazonVcWarehouseQueryParam } from "@app/views/warehouse/amazonVc/warehouse/config/filter";
import { mapAmazonVcWarehouseItem, type AmazonVcWarehouseItemApi, type AmazonVcWarehouseRow } from "@app/views/warehouse/amazonVc/warehouse/config/data";
import type { AmazonVcWarehouseSavePayload } from "@app/views/warehouse/amazonVc/warehouse/config/warehouse-dialog.data";
import {
    toAmazonVcLogisticsChannelExportRequestParams,
    toAmazonVcLogisticsChannelListRequestParams,
    type AmazonVcLogisticsChannelQueryParam,
} from "@app/views/warehouse/amazonVc/logisticsChannel/config/filter";
import { mapAmazonVcLogisticsChannelItem, type AmazonVcLogisticsChannelItemApi, type AmazonVcLogisticsChannelRow } from "@app/views/warehouse/amazonVc/logisticsChannel/config/data";
import type { AmazonVcLogisticsChannelSavePayload } from "@app/views/warehouse/amazonVc/logisticsChannel/config/channel-dialog.data";
import {
    mapLogisticsChannelMappingItem,
    type LogisticsChannelMappingChangeStatusResult,
    type LogisticsChannelMappingItemApi,
    type LogisticsChannelMappingQueryParam,
    type LogisticsChannelMappingRow,
} from "@app/views/warehouse/amazonVc/logisticsChannel/config/mapping-dialog.data";
import type { AmazonVcLogisticsMappingQueryParam } from "@app/views/warehouse/amazonVc/logisticsMapping/config/filter";
import { mapAmazonVcLogisticsMappingItem, type AmazonVcLogisticsMappingItemApi, type AmazonVcLogisticsMappingRow } from "@app/views/warehouse/amazonVc/logisticsMapping/config/data";
import {
    mapMappedProviderChannelItem,
    type MappedProviderChannelItemApi,
    type MappedProviderChannelQueryParam,
    type MappedProviderChannelRow,
} from "@app/views/warehouse/amazonVc/logisticsMapping/config/mapped-channels-dialog.data";
import {
    mapAddProviderChannelCandidateItem,
    type AddProviderChannelCandidateItemApi,
    type AddProviderChannelCandidateRow,
    type AddProviderChannelQueryParam,
} from "@app/views/warehouse/amazonVc/logisticsMapping/config/add-provider-channel-dialog.data";
import type { VcWarehouseMappingQueryParam } from "@app/views/warehouse/amazonVc/warehouseMapping/config/filter";
import { mapWarehousePlatformMappingItem, type VcWarehouseMappingChildRow, type WarehousePlatformMappingApiItem } from "@app/views/warehouse/amazonVc/warehouseMapping/config/data";
import {
    mapWarehouseMappingDialogItem,
    type AddVcWarehouseMappingItem,
    type WarehouseMappingChangeStatusResult,
    type WarehouseMappingDialogItemApi,
    type WarehouseMappingDialogQueryParam,
    type WarehouseMappingDialogRow,
} from "@app/views/warehouse/amazonVc/warehouseMapping/config/mapping-dialog.data";
import type { FetchListOptions, ListResult } from "@shared/types/list";

/** VC 仓库列表：POST /manage/warehouseAmazonVc/list */
export async function fetchAmazonVcWarehouseList(query: AmazonVcWarehouseQueryParam, options?: FetchListOptions): Promise<ListResult<AmazonVcWarehouseRow>> {
    const data = await http.post<{ items: AmazonVcWarehouseItemApi[]; total: number }>("/manage/warehouseAmazonVc/list", toAmazonVcWarehouseListRequestParams(query), { signal: options?.signal });
    return {
        list: (data.items ?? []).map(mapAmazonVcWarehouseItem),
        total: data.total ?? 0,
    };
}

/** 异步导出 Amazon VC 仓库，进入下载中心 */
export function exportAmazonVcWarehouseList(params: AmazonVcWarehouseQueryParam) {
    return http.post<{ taskId: string; downloadTaskId: string }>("/manage/warehouseAmazonVc/export", toAmazonVcWarehouseExportRequestParams(params));
}

/** 新增 / 编辑 AmazonVC 仓库 */
export function saveAmazonVcWarehouse(payload: AmazonVcWarehouseSavePayload) {
    if (payload.id != null) {
        return http.post<void>("/manage/warehouseAmazonVc/edit", payload);
    }
    return http.post<void>("/manage/warehouseAmazonVc/add", payload);
}

/** 批量设置仓库状态：POST /manage/warehouseAmazonVc/changeStatus，传 ids + status，返回批量结果 */
export function changeAmazonVcWarehouseStatus(ids: string[], status: number) {
    return http.post<BatchResultResponse>("/manage/warehouseAmazonVc/changeStatus", { ids, status });
}

/** 批量删除仓库：POST /manage/warehouseAmazonVc/delete，传 ids，返回批量结果 */
export function deleteAmazonVcWarehouse(ids: string[]) {
    return http.post<BatchResultResponse>("/manage/warehouseAmazonVc/delete", { ids });
}

/** VC 仓库下拉选项（/manage/warehouseAmazonVc/options） */
export interface AmazonVcWarehouseOptionItem {
    id: string;
    storeId: string;
    vcType: string;
    vcTypeLabel: string;
    warehouseCode: string;
    warehouseName: string;
    statusLabel: string;
}

/** VC 仓库下拉：POST /manage/warehouseAmazonVc/options，传 vcType（PO / DF） */
export async function fetchAmazonVcWarehouseOptions(vcType: string): Promise<AmazonVcWarehouseOptionItem[]> {
    const data = await http.post<{
        items: {
            id: string;
            storeId: string;
            vcType: string;
            vcTypeLabel?: string;
            warehouseCode: string;
            warehouseName: string;
            statusLabel?: string;
        }[];
    }>("/manage/warehouseAmazonVc/options", { vcType });
    return (data.items ?? []).map(item => ({
        id: item.id,
        storeId: item.storeId,
        vcType: item.vcType,
        vcTypeLabel: item.vcTypeLabel ?? item.vcType,
        warehouseCode: item.warehouseCode,
        warehouseName: item.warehouseName,
        statusLabel: item.statusLabel ?? "",
    }));
}

/** VC 物流渠道下拉选项（/manage/warehouseVcLogisticsChannel/options） */
export interface AmazonVcLogisticsChannelOptionItem {
    id: string;
    channelCode: string;
    channelName: string;
    vcType: string;
    vcTypeLabel: string;
    statusLabel: string;
}

/** VC 物流渠道下拉：POST /manage/warehouseVcLogisticsChannel/options，传 vcType（PO / DF） */
export async function fetchAmazonVcLogisticsChannelOptions(vcType: string): Promise<AmazonVcLogisticsChannelOptionItem[]> {
    const data = await http.post<{
        items: {
            id: string;
            channelCode?: string;
            channelName: string;
            vcType: string;
            vcTypeLabel?: string;
            statusLabel?: string;
        }[];
    }>("/manage/warehouseVcLogisticsChannel/options", { vcType });
    return (data.items ?? []).map(item => ({
        id: item.id,
        channelCode: item.channelCode ?? item.channelName,
        channelName: item.channelName,
        vcType: item.vcType,
        vcTypeLabel: item.vcTypeLabel ?? item.vcType,
        statusLabel: item.statusLabel ?? "",
    }));
}

/** 已添加仓库（名称/代码）：POST /manage/warehouseAmazonVc/options，传 vcType，用于新增/编辑时校验同店铺下仓库代码、仓库名称不能重复 */
export async function fetchAmazonVcWarehouseExisting(vcType: string): Promise<{ storeId: string; warehouseCode: string; warehouseName: string }[]> {
    const items = await fetchAmazonVcWarehouseOptions(vcType);
    return items.map(item => ({ storeId: item.storeId, warehouseCode: item.warehouseCode, warehouseName: item.warehouseName }));
}

/** VC Code 下拉：POST /manage/amazonVcCode/options；退件仓传 types=[PO,DF] 拿同店全部启用 Code */
export async function fetchAmazonVcCodeOptions(params: { storeId: string; types?: string[] }): Promise<{ id: string; code: string; name: string; type: string }[]> {
    const data = await http.post<{
        items: { id: string | number; code: string | null; name: string; type: string }[];
    }>("/manage/amazonVcCode/options", {
        storeId: [params.storeId],
        types: params.types ?? ["PO", "DF"],
    });
    return (data.items ?? []).map(item => ({
        id: String(item.id),
        code: item.code ?? "",
        name: item.name,
        type: item.type,
    }));
}

/** VC 仓库详情：POST /manage/warehouseAmazonVc/detail，传 id */
export interface AmazonVcWarehouseDetailApi {
    id: string;
    storeId: string;
    storeName: string;
    addressNumber?: string;
    vcType: string;
    vcTypeLabel?: string;
    warehouseCode: string;
    /** 文档个别接口可能拼写为 warehousenName，兼容两种 */
    warehouseName?: string;
    warehousenName?: string;
    regionCode: string;
    regionLabel: string;
    zoneCode: string;
    zoneLabel: string;
    countryCode: string;
    stateCode: string;
    cityId: number;
    cityName: string;
    district: string;
    address1: string;
    address2: string;
    zipCode: string;
    contactName: string;
    contactPhone: string;
    contactEmail: string;
    remark: string;
    status: number;
    statusLabel: string;
    hasMapping?: boolean;
    amazonVcCodes?: { id: string; code: string; name: string; type: string; typeLabel?: string }[];
    createTime: string;
    updateTime: string;
}

export function fetchAmazonVcWarehouseDetail(id: string) {
    return http.post<AmazonVcWarehouseDetailApi>("/manage/warehouseAmazonVc/detail", { id });
}

/** 区域下拉：POST /manage/geo/region/options，返回 { code, name }，映射为 { label, value } */
export async function fetchGeoRegionOptions(): Promise<{ label: string; value: string }[]> {
    const data = await http.post<{ items: { code: string; name: string }[] }>("/manage/geo/region/options");
    return (data.items ?? []).map(item => ({ label: item.name, value: item.code }));
}

/** 国家下拉：POST /manage/geo/country/options，传 regionCode（区域 code，可选，不传返回全量）*/
export async function fetchGeoCountryOptions(regionCode?: string): Promise<{ label: string; value: string }[]> {
    const data = await http.post<{ items: { code: string; name: string }[] }>("/manage/geo/country/options", regionCode ? { regionCode } : {});
    return (data.items ?? []).map(item => ({ label: item.name, value: item.code }));
}

/** 省/州下拉：POST /manage/geo/state/options，传 regionCode（国家 code），返回 { code, name } */
export async function fetchGeoStateOptions(regionCode: string): Promise<{ label: string; value: string }[]> {
    const data = await http.post<{ items: { code: string; name: string }[] }>("/manage/geo/state/options", { regionCode });
    // 页面 provinceOptions 需要展示 code（而不是 name）
    return (data.items ?? []).map(item => ({ label: item.code, value: item.code }));
}

/** 城市下拉：POST /manage/geo/city/options，传 regionCode（国家 code）+ stateCode（省/州 code） */
export async function fetchGeoCityOptions(params: { regionCode: string; stateCode: string }): Promise<{ label: string; value: number }[]> {
    const data = await http.post<{ items: { id: number; name: string }[] }>("/manage/geo/city/options", {
        regionCode: params.regionCode,
        stateCode: params.stateCode,
    });
    return (data.items ?? []).map(item => ({ label: item.name, value: item.id }));
}

/** 分区下拉：POST /manage/geo/zone/options，传 regionCode + countryCode（均可选，不传返回全量） */
export async function fetchGeoZoneOptions(params: { regionCode?: string; countryCode?: string } = {}): Promise<{ label: string; value: string }[]> {
    const body: Record<string, string> = {};
    if (params.regionCode) body.regionCode = params.regionCode;
    if (params.countryCode) body.countryCode = params.countryCode;
    const data = await http.post<{ items: { code: string; name: string }[] }>("/manage/geo/zone/options", body);
    return (data.items ?? []).map(item => ({ label: item.name, value: item.code }));
}

/** VC 物流渠道列表：POST /manage/warehouseVcLogisticsChannel/list */
export async function fetchAmazonVcLogisticsChannelList(query: AmazonVcLogisticsChannelQueryParam, options?: FetchListOptions): Promise<ListResult<AmazonVcLogisticsChannelRow>> {
    const data = await http.post<{ items: AmazonVcLogisticsChannelItemApi[]; total: number }>("/manage/warehouseVcLogisticsChannel/list", toAmazonVcLogisticsChannelListRequestParams(query), {
        signal: options?.signal,
    });
    return {
        list: (data.items ?? []).map(mapAmazonVcLogisticsChannelItem),
        total: data.total ?? 0,
    };
}

/** 异步导出 VC 物流渠道，进入下载中心 */
export function exportAmazonVcLogisticsChannelList(params: AmazonVcLogisticsChannelQueryParam) {
    return http.post<{ taskId: string; downloadTaskId: string }>("/manage/warehouseVcLogisticsChannel/export", toAmazonVcLogisticsChannelExportRequestParams(params));
}

/** 新增 / 编辑 VC 物流渠道 */
export function saveAmazonVcLogisticsChannel(payload: AmazonVcLogisticsChannelSavePayload) {
    if (payload.id != null) {
        return http.post<void>("/manage/warehouseVcLogisticsChannel/edit", payload);
    }
    return http.post<void>("/manage/warehouseVcLogisticsChannel/add", payload);
}

async function fetchAmazonVcLogisticsChannelMappingByUrl(
    url: string,
    query: LogisticsChannelMappingQueryParam,
    options?: FetchListOptions,
    mapOptions?: { forceMapped?: boolean },
): Promise<ListResult<LogisticsChannelMappingRow>> {
    const body: Record<string, unknown> = {
        vcLogisticsChannelId: String(query.channelId),
        // 后端要求数组：添加 [1]，编辑 [2]
        pairStatus: [query.pairStatus],
    };
    if (query.corpId !== "" && query.corpId != null) {
        body.corpId = String(query.corpId);
    }
    if (query.keyword.trim()) {
        body.keyword = query.keyword.trim();
    }

    const data = await http.post<{ items: LogisticsChannelMappingItemApi[]; total: number }>(url, body, { signal: options?.signal });
    const list = (data.items ?? []).map(item => mapLogisticsChannelMappingItem(item, mapOptions));
    // 接口未提供分页参数，前端按 page/limit 切片
    const start = (query.page - 1) * query.limit;
    return {
        list: list.slice(start, start + query.limit),
        total: data.total ?? list.length,
    };
}

/**
 * VC 物流渠道映射列表（添加 / 编辑共用）：
 * POST /manage/warehouseVcLogisticsChannel/mapping/candidateList
 * - 添加：pairStatus=1
 * - 编辑：pairStatus=2（行视为已映射，操作展示解除映射）
 */
export async function fetchAmazonVcLogisticsChannelMappingCandidateList(query: LogisticsChannelMappingQueryParam, options?: FetchListOptions): Promise<ListResult<LogisticsChannelMappingRow>> {
    return fetchAmazonVcLogisticsChannelMappingByUrl("/manage/warehouseVcLogisticsChannel/mapping/candidateList", query, options, {
        forceMapped: query.pairStatus === 2,
    });
}

/** 添加物流映射：POST /manage/warehouseVcLogisticsChannel/mapping/add */
export function addAmazonVcLogisticsChannelMapping(payload: { vcLogisticsChannelId: string; warehouseChannelId: string }) {
    return http.post<LogisticsChannelMappingChangeStatusResult>("/manage/warehouseVcLogisticsChannel/mapping/add", {
        vcLogisticsChannelId: String(payload.vcLogisticsChannelId),
        warehouseChannelId: String(payload.warehouseChannelId),
    });
}

/** 映射状态变更：POST /manage/warehouseVcLogisticsChannel/mapping/changeStatus，status=1 启用 / status=2 解除 */
export function changeAmazonVcLogisticsChannelMappingStatus(ids: number[], status: number) {
    return http.post<LogisticsChannelMappingChangeStatusResult>("/manage/warehouseVcLogisticsChannel/mapping/changeStatus", { ids, status });
}

/**
 * VC 物流映射列表：复用物流渠道 list 接口（含 mappingCount；DF 可带 trackingNumberPrefix）
 * POST /manage/warehouseVcLogisticsChannel/list
 */
export async function fetchAmazonVcLogisticsMappingList(query: AmazonVcLogisticsMappingQueryParam, options?: FetchListOptions): Promise<ListResult<AmazonVcLogisticsMappingRow>> {
    const body: Record<string, unknown> = {
        page: query.page,
        perPage: query.limit,
        platformCode: query.platform || undefined,
        // Tab / 筛选已是数组时直接透传；scalar 也会被 normalize 成数组
        vcType: resolveMultiSelectForRequest(query.vcType as unknown as (string | number)[]),
    };
    const channelName = resolveCombinedSearchForRequest(query.name as unknown as string | string[]);
    if (channelName !== undefined) body.channelName = channelName;

    const data = await http.post<{ items: AmazonVcLogisticsMappingItemApi[]; total: number }>("/manage/warehouseVcLogisticsChannel/list", body, {
        signal: options?.signal,
    });
    return {
        list: (data.items ?? []).map(mapAmazonVcLogisticsMappingItem),
        total: data.total ?? 0,
    };
}

/**
 * 已映射服务商物流渠道列表：
 * POST /manage/warehouseVcLogisticsChannel/mapping/list
 */
export async function fetchMappedProviderChannelList(query: MappedProviderChannelQueryParam, options?: FetchListOptions): Promise<ListResult<MappedProviderChannelRow>> {
    const body: Record<string, unknown> = {
        page: query.page,
        perPage: query.limit,
        vcLogisticsChannelId: String(query.channelId),
    };
    const data = await http.post<{ items: MappedProviderChannelItemApi[]; total: number }>("/manage/warehouseVcLogisticsChannel/mapping/list", body, {
        signal: options?.signal,
    });
    return {
        list: (data.items ?? []).map((item, index) => mapMappedProviderChannelItem(item, index)),
        total: data.total ?? 0,
    };
}

/**
 * 添加服务商物流渠道弹窗 — 服务商下拉：
 * POST /manage/warehouse/provider/options
 */
export async function fetchWarehouseProviderSelectOptions(): Promise<Array<{ label: string; value: string }>> {
    const data = await http.post<{ items: Array<{ providerCode?: string; providerName?: string }> }>("/manage/warehouse/provider/options", {});
    return (data.items ?? [])
        .filter((item): item is { providerCode: string; providerName?: string } => Boolean(item.providerCode))
        .map(item => ({
            label: item.providerName || item.providerCode,
            value: item.providerCode,
        }));
}

/**
 * 添加服务商物流渠道映射：
 * POST /manage/warehouseVcLogisticsChannel/mapping/add
 */
export function addVcLogisticsChannelMapping(vcLogisticsChannelId: string | number, providerChannelIds: number[]) {
    return http.post<BatchResultResponse>("/manage/warehouseVcLogisticsChannel/mapping/add", {
        vcLogisticsChannelId: String(vcLogisticsChannelId),
        providerChannelIds,
    });
}

/**
 * 移除服务商物流渠道映射：
 * POST /manage/warehouseVcLogisticsChannel/mapping/remove
 */
export function removeVcLogisticsChannelMapping(ids: number[]) {
    return http.post<BatchResultResponse>("/manage/warehouseVcLogisticsChannel/mapping/remove", { ids });
}

/**
 * 添加服务商物流渠道候选列表：
 * POST /manage/warehouseVcLogisticsChannel/mapping/candidateList
 */
export async function fetchAddProviderChannelCandidateList(query: AddProviderChannelQueryParam, options?: FetchListOptions): Promise<ListResult<AddProviderChannelCandidateRow>> {
    const body: Record<string, unknown> = {
        page: query.page,
        perPage: query.limit,
        vcLogisticsChannelId: String(query.channelId),
    };
    if (query.providerId) body.providerCode = query.providerId;
    const channelCode = resolveCombinedSearchForRequest(query.channelCode as unknown as string | string[]);
    if (channelCode !== undefined) body.channelCode = channelCode;

    const data = await http.post<{ items: AddProviderChannelCandidateItemApi[]; total: number }>("/manage/warehouseVcLogisticsChannel/mapping/candidateList", body, { signal: options?.signal });
    return {
        list: (data.items ?? []).map(mapAddProviderChannelCandidateItem),
        total: data.total ?? 0,
    };
}

/** VC 仓库映射列表：POST /manage/warehousePlatformMapping/list */
export async function fetchVcWarehouseMappingList(query: VcWarehouseMappingQueryParam, options?: FetchListOptions): Promise<ListResult<VcWarehouseMappingChildRow>> {
    const body: Record<string, unknown> = {
        page: query.page,
        perPage: query.limit,
        channelType: query.channelType || undefined,
        platformStoreId: resolveMultiSelectForRequest(query.platformStoreId),
        regionCode: resolveMultiSelectForRequest(query.region),
        countryCode: resolveMultiSelectForRequest(query.country),
        zoneCode: resolveMultiSelectForRequest(query.zone),
    };
    // warehouseCode 为 UiCombinedSelect：模糊 string / 精确 string[]
    const warehouseCode = resolveCombinedSearchForRequest(query.warehouseCode as unknown as string | string[]);
    if (warehouseCode !== undefined) body.warehouseCode = warehouseCode;

    const data = await http.post<{ items: WarehousePlatformMappingApiItem[]; total: number }>("/manage/warehousePlatformMapping/list", body, {
        signal: options?.signal,
    });
    return {
        list: (data.items ?? []).flatMap(item => mapWarehousePlatformMappingItem(item, query.channelType)),
        total: data.total ?? 0,
    };
}

async function fetchVcWarehouseMappingDialogByUrl(
    url: string,
    query: WarehouseMappingDialogQueryParam,
    options?: FetchListOptions,
    mapOptions?: { forceMapped?: boolean },
): Promise<ListResult<WarehouseMappingDialogRow>> {
    const body: Record<string, unknown> = {
        page: query.page,
        perPage: query.limit,
        platformWarehouseId: String(query.platformWarehouseId),
        keyword: query.keyword.trim(),
    };
    if (query.corpId !== "" && query.corpId != null) {
        body.corpId = String(query.corpId);
    }

    const data = await http.post<{ items: WarehouseMappingDialogItemApi[]; total: number }>(url, body, { signal: options?.signal });
    return {
        list: (data.items ?? []).map(item => mapWarehouseMappingDialogItem(item, mapOptions)),
        total: data.total ?? 0,
    };
}

/**
 * 仓库映射弹窗列表（添加 / 编辑共用）：
 * - 添加：pairStatus=[1]，POST /manage/warehousePlatformMapping/external/candidateList
 * - 编辑：pairStatus=[2]（行视为已映射，操作展示解除映射），POST /manage/warehousePlatformMapping/external/mapping/list
 */
export async function fetchVcWarehouseMappingCandidateList(query: WarehouseMappingDialogQueryParam, options?: FetchListOptions): Promise<ListResult<WarehouseMappingDialogRow>> {
    const url = query.pairStatus === 2 ? "/manage/warehousePlatformMapping/external/mapping/list" : "/manage/warehousePlatformMapping/external/candidateList";
    return fetchVcWarehouseMappingDialogByUrl(url, query, options, {
        forceMapped: query.pairStatus === 2,
    });
}

/** 添加仓库映射：POST /manage/warehousePlatformMapping/add，items 数组支持批量提交 */
export function addVcWarehouseMapping(items: AddVcWarehouseMappingItem[]) {
    return http.post<WarehouseMappingChangeStatusResult>("/manage/warehousePlatformMapping/add", { items });
}

/** 解除仓库映射：POST /manage/warehousePlatformMapping/remove，ids 支持单个 / 批量 */
export function removeVcWarehouseMapping(ids: number[]) {
    return http.post<WarehouseMappingChangeStatusResult>("/manage/warehousePlatformMapping/remove", { ids });
}
